import QRCode from 'qrcode';

const LOGO_SRC = '/logo y texto cadencya.png';

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`No se pudo cargar la imagen: ${src}`));
    img.src = src;
  });
}

function wrapText(ctx, text, maxWidth) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  if (!words.length) return [''];

  const lines = [];
  let current = words[0];
  for (let i = 1; i < words.length; i += 1) {
    const next = `${current} ${words[i]}`;
    if (ctx.measureText(next).width <= maxWidth) {
      current = next;
    } else {
      lines.push(current);
      current = words[i];
    }
  }
  lines.push(current);
  return lines;
}

/**
 * Genera un PNG con logo Cadencya, título "Repertorio - {bandName}", opcionalmente la contraseña
 * del enlace y código QR.
 * @param {{ url: string, bandName: string, sharePassword?: string }} params
 * @returns {Promise<Blob>}
 */
export async function generateRepertorioShareQrPng({ url, bandName, sharePassword }) {
  if (!url?.trim()) throw new Error('URL vacía');

  const qrSize = 400;
  const padding = 40;
  // Logo más visible respecto al QR (antes ~72px de alto quedaba demasiado pequeño).
  const logoMaxW = 380;
  const logoMaxH = 120;
  const title = `Repertorio - ${(bandName || '').trim() || 'tu banda'}`;

  const qrCanvas = document.createElement('canvas');
  await QRCode.toCanvas(qrCanvas, url.trim(), {
    width: qrSize,
    margin: 2,
    errorCorrectionLevel: 'M',
    color: { dark: '#1A202C', light: '#FFFFFF' },
  });

  const logo = await loadImage(LOGO_SRC);
  const logoScale = Math.min(logoMaxW / logo.width, logoMaxH / logo.height, 1);
  const logoW = logo.width * logoScale;
  const logoH = logo.height * logoScale;

  const canvasW = Math.max(qrSize + padding * 2, logoMaxW + padding * 2, 420);
  const textMaxW = canvasW - padding * 2;

  const measureCanvas = document.createElement('canvas');
  const measureCtx = measureCanvas.getContext('2d');
  const titleFont = '600 22px system-ui, -apple-system, "Segoe UI", sans-serif';
  const passwordFont = '600 20px system-ui, -apple-system, "Segoe UI", sans-serif';

  measureCtx.font = titleFont;
  const titleLines = wrapText(measureCtx, title, textMaxW);
  const titleLineHeight = 30;
  const titleBlockH = titleLines.length * titleLineHeight;

  const pwdText = sharePassword?.trim() ? `Contraseña: ${sharePassword.trim()}` : '';
  let passwordLines = [];
  let passwordBlockH = 0;
  if (pwdText) {
    measureCtx.font = passwordFont;
    passwordLines = wrapText(measureCtx, pwdText, textMaxW);
    const passwordLineHeight = 26;
    passwordBlockH = 16 + passwordLines.length * passwordLineHeight;
  }

  const canvasH = padding + logoH + 24 + titleBlockH + passwordBlockH + 28 + qrSize + padding;

  const canvas = document.createElement('canvas');
  canvas.width = canvasW;
  canvas.height = canvasH;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvasW, canvasH);

  let y = padding;
  // Fondo blanco explícito bajo el logo (PNG con transparencia).
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect((canvasW - logoW) / 2 - 8, y - 8, logoW + 16, logoH + 16);
  ctx.drawImage(logo, (canvasW - logoW) / 2, y, logoW, logoH);
  y += logoH + 24;

  ctx.fillStyle = '#2D3748';
  ctx.font = titleFont;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  titleLines.forEach((line) => {
    ctx.fillText(line, canvasW / 2, y);
    y += titleLineHeight;
  });

  if (passwordLines.length) {
    y += 16;
    ctx.fillStyle = '#6B46C1';
    ctx.font = passwordFont;
    const passwordLineHeight = 26;
    passwordLines.forEach((line) => {
      ctx.fillText(line, canvasW / 2, y);
      y += passwordLineHeight;
    });
  }

  y += 28;
  ctx.drawImage(qrCanvas, (canvasW - qrSize) / 2, y, qrSize, qrSize);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) reject(new Error('No se pudo generar el PNG'));
      else resolve(blob);
    }, 'image/png');
  });
}

export function downloadBlob(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}
